import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { ApiService } from '@services/api/api.service';
import { LeadsService } from '@services/api/leads.service';
import { UserService } from '@services/api/user.service';
import { Crypto } from '@services/helpers/encryption-utils';
import { LocalStorageService } from '@services/helpers/local-storage.service';
import { ToasterService } from '@services/util/toaster.service';
import { OverlayPanel } from 'primeng/overlaypanel';
import { BehaviorSubject, catchError, combineLatest, forkJoin, map, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { displayValue, DUPLICATE_LEAD_STATUS, leadName, ownerName, requiredDefaultsFrom, toLeadUpdateBody } from './helpers/merge.helpers';
import { CONTACT_FIELD_KEYS } from './models/lead-field-registry';
import { DuplicateLeadMergeStore, MergeWizardState } from './services/duplicate-lead-merge.store';
import { LeadComparisonService } from './services/lead-comparison.service';
import { LeadMergeService } from './services/lead-merge.service';
import { ComparisonCell, ComparisonRow, DuplicateLead, FieldKind, LeadId, MergeResult, SkipResult } from './models/duplicate-lead-merge.types';

/** One entry in the "keep the lead" dropdown. */
export interface KeepLeadOption {
  value: LeadId;
  label: string;
  createdAt?: string;
  status: string;
  statusClass: string;
  recommended: boolean;
}

/**
 * How one lead's value sits in a compare row:
 * - selected: the value the main lead ends up with (green tick)
 * - same: identical to the selected value, so nothing to pick (grey tick)
 * - available: a different value the agent can tick instead
 * - blank: the lead has nothing for this field
 * - fixed: shown for reference only (locked owner row, alternate details, unresolved values)
 */
export type GridCellState = 'selected' | 'same' | 'available' | 'blank' | 'fixed';

export interface GridCell {
  display: string;
  different: boolean;
  state: GridCellState;
  /** Name/email/mobile of a duplicate that differs, so it can go on the contact instead. */
  canAlternate: boolean;
  isAlternate: boolean;
}

export interface GridRow {
  key: string;
  label: string;
  /** Owner can't be picked — the lead always stays with the original owner. */
  locked: boolean;
  cells: Record<LeadId, GridCell>;
}

export interface MergeGrid {
  rows: GridRow[];
  /** Ticked fields per lead, for the "Lead 1 · 3 Fields Selected" summary. */
  counts: Record<LeadId, number>;
}

/**
 * "Duplicate leads found" — a self-contained, single-screen merge modal.
 *
 * Trigger it by binding `[(visible)]` and passing the currently open lead via
 * `sourceLeadId`. It fetches the same-person duplicates, renders them side by
 * side in a compare grid and pre-selects the original (oldest) lead as the main lead. The agent
 * ticks, field by field, whose value the main lead keeps; on merge the other leads close as
 * duplicates. The host owns tab-closing and refreshing the kept lead.
 *
 * The grid scrolls sideways rather than wrapping: a person can legitimately have
 * dozens of duplicate enquiries, and the action bar below must stay reachable.
 */
@Component({
  selector: 'app-duplicate-lead-merge',
  templateUrl: './duplicate-lead-merge.component.html',
  styleUrls: ['./duplicate-lead-merge.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DuplicateLeadMergeStore, LeadComparisonService, LeadMergeService],
})
export class DuplicateLeadMergeComponent implements OnChanges, OnDestroy {
  /** Two-way `[(visible)]`. Opening (false → true) loads the duplicate set. */
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  /** The lead currently open / being marked-duplicate / closed. Used to fetch peers. */
  @Input() sourceLeadId: LeadId | null = null;

  /** Optional pre-fetched full detail of the source lead (skips one fetch). */
  @Input() sourceLead: DuplicateLead | null = null;

  /** Optional pre-fetched full candidate set. When provided, no fetching occurs. */
  @Input() leads: DuplicateLead[] | null = null;

  /** Teams the signed-in user belongs to; a lead owned by one of them counts as theirs. */
  @Input() userTeamIds: string[] = [];

  @Output() merged = new EventEmitter<MergeResult>();
  @Output() skipped = new EventEmitter<SkipResult>();
  @Output() cancelled = new EventEmitter<void>();

  readonly state$ = this.store.state$;
  readonly keepLead$ = this.store.keepLead$;
  readonly duplicates$ = this.store.duplicates$;

  /** Options for the "Set As Main Lead" select — rebuilt whenever candidates load. */
  leadOptions: KeepLeadOption[] = [];

  /** Duplicate leads whose name/email/mobile are saved on the kept lead's contact as an alternate contact. */
  readonly alternateLeadIds$ = new BehaviorSubject<LeadId[]>([]);

  /** Compare grid with each cell's tick state, recomputed on every selection change. */
  readonly grid$: Observable<MergeGrid> = combineLatest([this.store.state$, this.alternateLeadIds$]).pipe(
    map(([state, alternateIds]) => this.buildGrid(state, alternateIds))
  );

  /** Duplicate lead whose cell opened the alternative-details menu. */
  menuLeadId: LeadId | null = null;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly store: DuplicateLeadMergeStore,
    private readonly api: ApiService,
    private readonly localStorageService: LocalStorageService,
    private readonly leadsApi: LeadsService,
    private readonly userService: UserService,
    private readonly mergeService: LeadMergeService,
    private readonly toaster: ToasterService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible && !changes['visible'].previousValue) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---- data loading -------------------------------------------------------

  private load(): void {
    this.store.reset();
    this.leadOptions = [];
    this.alternateLeadIds$.next([]);

    if (this.leads?.length) {
      this.applyCandidates(this.sameProjectOnly(this.dedupeById(this.leads)));
      return;
    }
    if (!this.sourceLeadId) {
      this.store.setError('No lead supplied to review.');
      return;
    }

    this.store.setLoading(true);
    this.leadsApi
      .getLeadSamePersonPrompt(this.sourceLeadId)
      .pipe(
        catchError(() => of({ status: false, data: [] as any[] })),
        takeUntil(this.destroy$)
      )
      .subscribe(res => {
        const dupIds = (res?.data ?? []).map((l: any) => l?._id).filter(Boolean) as LeadId[];
        const ids = this.dedupeIds([this.sourceLeadId as LeadId, ...dupIds]);
        this.fetchLeadDetails(ids);
      });
  }

  /** Fetch full detail for each candidate, reusing any pre-supplied leads. */
  private fetchLeadDetails(ids: LeadId[]): void {
    const prefetched = new Map<LeadId, DuplicateLead>();
    if (this.sourceLead?._id) prefetched.set(this.sourceLead._id, this.sourceLead);
    (this.leads ?? []).forEach(l => l?._id && prefetched.set(l._id, l));

    const requests: Observable<DuplicateLead | null>[] = ids.map(id =>
      prefetched.has(id)
        ? of(prefetched.get(id) as DuplicateLead)
        : this.leadsApi.getLeadById(id).pipe(
            catchError(() => of(null)),
            takeUntil(this.destroy$)
          )
    );

    forkJoin(requests)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: results => {
          const loaded = results.map(r => this.normalizeLead(r)).filter((l): l is DuplicateLead => !!l?._id);
          const candidates = this.sameProjectOnly(this.dedupeById(loaded));
          this.applyCandidates(candidates);
          this.store.setLoading(false);
          if (candidates.length < 2) {
            this.store.setError('No other duplicate leads were found for this person in this project.');
          }
        },
        error: () => {
          this.store.setLoading(false);
          this.store.setError('Unable to load duplicate leads. Please try again.');
        },
      });
  }

  /**
   * Client rule: duplicates are only closed within the same project. A lead for the same person
   * in another project is a separate enquiry, so it never enters the modal.
   */
  private sameProjectOnly(candidates: DuplicateLead[]): DuplicateLead[] {
    const source = candidates.find(lead => lead._id === this.sourceLeadId) ?? candidates[0];
    const projectKey = (lead: DuplicateLead): string => {
      const value = lead?.project || lead?.listing || lead?.property;
      return String(value?._id ?? value ?? '');
    };
    const sourceKey = projectKey(source);
    return candidates.filter(lead => projectKey(lead) === sourceKey);
  }

  /**
   * Seed the store, build the picker, and pre-select the recommended lead so the
   * tick list renders immediately instead of behind a first step.
   */
  private applyCandidates(candidates: DuplicateLead[]): void {
    this.store.setCandidates(candidates);
    if (candidates.length < 2) {
      this.leadOptions = [];
      return;
    }
    const recommendedId = this.oldestOf(candidates)._id;
    this.leadOptions = candidates.map((lead, index) => ({
      value: lead._id,
      label: `Lead ${index + 1}`,
      createdAt: lead.created_at,
      status: displayValue(lead.lead_status),
      statusClass: this.badgeClass(lead),
      recommended: lead._id === recommendedId,
    }));
    this.chooseKeep(recommendedId);
  }

  /**
   * Client rule: the oldest enquiry is the original lead — always the recommended main lead, and
   * its owner keeps the lead whichever lead is kept.
   */
  private oldestOf(candidates: DuplicateLead[]): DuplicateLead {
    return candidates.reduce((oldest, lead) =>
      new Date(lead.created_at ?? 0).getTime() < new Date(oldest.created_at ?? 0).getTime() ? lead : oldest
    );
  }

  /** `GET /lead-detail-by-id/:id` may wrap the lead in `{ data }` — unwrap it. */
  private normalizeLead(res: any): DuplicateLead | null {
    if (!res) return null;
    const lead = res.data ?? res.lead ?? res;
    return lead && lead._id ? (lead as DuplicateLead) : null;
  }

  private dedupeIds(ids: LeadId[]): LeadId[] {
    return Array.from(new Set(ids.filter(Boolean)));
  }

  private dedupeById(leads: DuplicateLead[]): DuplicateLead[] {
    const map = new Map<LeadId, DuplicateLead>();
    leads.forEach(l => l?._id && map.set(l._id, l));
    return Array.from(map.values());
  }

  // ---- lead to keep -------------------------------------------------------

  chooseKeep(leadId: LeadId): void {
    // Re-picking the main lead would rebuild the comparison and wipe the agent's ticks.
    if (!leadId || leadId === this.store.snapshot.keepLeadId) return;
    this.store.chooseKeep(leadId);
    // The kept lead can't be its own alternate.
    this.setAlternate(leadId, false);
    this.applyOriginalOwner();
  }

  /**
   * Client rule: the lead is always owned by the original owner. When another lead is the main
   * one, its owner is replaced with the original lead's (locked in the grid), and the agent
   * responsible defaults to the original's too — that one the agent can still change.
   */
  private applyOriginalOwner(): void {
    const original = this.originalLead;
    const keepId = this.store.snapshot.keepLeadId;
    const scalars = this.store.snapshot.comparison?.scalars ?? [];
    if (!original || original._id === keepId) return;

    if (scalars.some(field => field.key === 'owner') && ownerName(original)) {
      this.store.setScalarValue('owner', this.pickKeys(original, ['Owner', 'owner', 'Owner_formatted', 'isOwnerTeam']), original._id);
    }
    const agentKeys = ['agent_responsible', 'agent_responsible_name', 'agent_responsible_formatted', 'isAgentResponsibleTeam'];
    if (scalars.some(field => field.key === 'agent_responsible') && original.agent_responsible) {
      this.store.setScalarValue('agent_responsible', this.pickKeys(original, agentKeys), original._id);
    }
  }

  private pickKeys(lead: DuplicateLead, keys: string[]): Record<string, any> {
    const out: Record<string, any> = {};
    keys.forEach(key => {
      if (lead[key] !== undefined && lead[key] !== null) out[key] = lead[key];
    });
    return out;
  }

  // ---- who may close ------------------------------------------------------

  /** The first enquiry — whoever owns it is the person the duplicates belong to. */
  get originalLead(): DuplicateLead | null {
    const candidates = this.store.snapshot.candidates;
    return candidates.length ? this.oldestOf(candidates) : null;
  }

  get originalOwnerName(): string {
    return (this.originalLead && ownerName(this.originalLead)) || 'the owner of the original lead';
  }

  /**
   * Client rule: an agent can't close someone else's lead as a duplicate — only the owner of the
   * original lead (directly or through their team) can, plus master admins and operations managers.
   */
  get canCloseDuplicates(): boolean {
    const role = (Crypto.decrypt(this.localStorageService.GetItem('role')) ?? '').toLowerCase();
    if (role === 'master admin' || role === 'operations manager') return true;
    const original = this.originalLead;
    const ownerId = String(original?.Owner?._id ?? original?.Owner ?? '');
    if (!ownerId) return false;
    if (ownerId === String(this.api.getUserId())) return true;
    return !!original?.isOwnerTeam && this.userTeamIds.includes(ownerId);
  }

  // ---- alternative details -----------------------------------------------

  isAlternate(leadId: LeadId): boolean {
    return this.alternateLeadIds$.value.includes(leadId);
  }

  /**
   * The kept lead's contact. `contactid` is dropped off the open lead once its assignments load,
   * so `contact_detail` is checked first (same order as the lead detail's Linked Contact card).
   */
  get keepContactId(): string | null {
    const lead = this.store.keepLead;
    const contactId = lead?.contact_detail?.[0]?._id ?? lead?.contactid?._id ?? lead?.contactid;
    return contactId ? String(contactId) : null;
  }

  openAlternateMenu(event: MouseEvent, leadId: LeadId, menu: OverlayPanel): void {
    this.menuLeadId = leadId;
    menu.toggle(event);
  }

  removeAlternate(leadId: LeadId): void {
    this.setAlternate(leadId, false);
  }

  toggleMenuAlternate(menu: OverlayPanel): void {
    if (this.menuLeadId && this.keepContactId) {
      this.setAlternate(this.menuLeadId, !this.isAlternate(this.menuLeadId));
    }
    menu.hide();
  }

  /**
   * Marking a lead as an alternate drops any name/email/mobile copy picked from it — the same
   * value can't both overwrite the kept lead and sit on the contact as someone else's details.
   */
  private setAlternate(leadId: LeadId, isOn: boolean): void {
    const current = this.alternateLeadIds$.value.filter(id => id !== leadId);
    this.alternateLeadIds$.next(isOn ? [...current, leadId] : current);
    if (!isOn) return;
    const selection = this.store.snapshot.scalarSelection;
    CONTACT_FIELD_KEYS.forEach(key => {
      if (selection[key]?.selected && selection[key].sourceId === leadId) {
        this.store.setScalarSelected(key, false);
      }
    });
  }

  /**
   * Saves each alternate through convert-to-contact with `add_as_alternate`, which writes the lead's
   * name/email/mobile into the contact's `alternate_contacts` without touching the contact's own
   * details (docs/backend/REM-3497_ALTERNATE_CONTACTS_FRONTEND.md). The reply is the contact as it
   * was before the link, and a lead already linked elsewhere gets "already assigned" with nothing
   * saved, so success is confirmed by re-reading the contact.
   */
  private saveAlternates(onSaved: () => void): void {
    const contactId = this.keepContactId;
    const leadIds = this.alternateLeadIds$.value;
    if (!contactId || !leadIds.length) {
      onSaved();
      return;
    }
    const links = leadIds.map(leadId =>
      this.leadsApi
        .convertLeadToContact(leadId, { contactid: contactId, createnew: false, add_as_alternate: true, alternate_label: null })
        .pipe(catchError(() => of(null)))
    );
    this.store.setSaving(true);
    forkJoin(links)
      .pipe(
        switchMap(() => this.userService.getUserById(contactId)),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: response => {
          const savedIds = (response?.data?.alternate_contacts ?? []).map(item => String(item?.lead_id ?? ''));
          const missing = leadIds.filter(leadId => !savedIds.includes(String(leadId)));
          if (missing.length) {
            this.store.setSaving(false);
            this.toaster.Error(`Could Not Save Alternative Details For ${missing.map(this.leadLabel).join(', ')}. Please Try Again Later.`);
            return;
          }
          // Closing a duplicate PUTs its whole loaded object; a stale `contactid: null` there would undo the link just made.
          this.store.snapshot.candidates.filter(lead => leadIds.includes(lead._id)).forEach(lead => (lead.contactid = contactId));
          onSaved();
        },
        error: () => {
          this.store.setSaving(false);
          this.toaster.Error('Could Not Save Alternative Details. Please Try Again Later.');
        },
      });
  }

  // ---- per-field selection -------------------------------------------------

  /**
   * One row per differing field (plus name/email/mobile, always shown). For each row the main lead
   * keeps exactly one value — its own unless another lead's is ticked. Notes/tags are lists, so
   * several leads can be ticked at once; unticking the main lead there replaces its own entries.
   */
  private buildGrid(state: MergeWizardState, alternateIds: LeadId[]): MergeGrid {
    const comparison = state.comparison;
    const keepId = state.keepLeadId;
    const counts: Record<LeadId, number> = {};
    state.candidates.forEach(lead => (counts[lead._id] = 0));
    if (!comparison || !keepId) return { rows: [], counts };

    const normalize = (text: string): string => (text ?? '').trim().toLowerCase();
    const contactRows = comparison.rows.filter(row => CONTACT_FIELD_KEYS.has(row.key));
    const hasNewContactDetails = (leadId: LeadId): boolean =>
      contactRows.some(row => !this.cellOf(row, leadId).blank && this.cellOf(row, leadId).different);

    const rows = comparison.rows.map(row => {
      const locked = row.key === 'owner';
      const cells: Record<LeadId, GridCell> = {};
      const scalar = comparison.scalars.find(field => field.key === row.key);
      const mergeable = comparison.mergeables.find(field => field.key === row.key);
      const selection = state.scalarSelection[row.key];
      const chosenId = selection?.selected ? selection.sourceId : keepId;
      const chosenDisplay = normalize(this.cellOf(row, chosenId).display);
      const entryMap = state.entrySelection[row.key] ?? {};

      for (const lead of state.candidates) {
        const cell = this.cellOf(row, lead._id);
        const isAlternate = CONTACT_FIELD_KEYS.has(row.key) && alternateIds.includes(lead._id);
        let cellState: GridCellState;

        if (cell.blank) {
          cellState = 'blank';
        } else if (row.kind === FieldKind.Mergeable) {
          if (lead._id === keepId) {
            cellState = !mergeable || (state.entryMode[row.key] ?? 'merge') === 'merge' ? 'selected' : 'available';
          } else {
            const entries = mergeable?.candidates.filter(entry => entry.source._id === lead._id) ?? [];
            cellState = !entries.length ? 'same' : entries.every(entry => entryMap[entry.id]) ? 'selected' : 'available';
          }
        } else if (lead._id === chosenId) {
          cellState = 'selected';
        } else if (normalize(cell.display) === chosenDisplay) {
          cellState = 'same';
        } else if (locked || isAlternate) {
          cellState = 'fixed';
        } else if (lead._id === keepId || scalar?.alternatives.some(alt => normalize(alt.display) === normalize(cell.display))) {
          cellState = 'available';
        } else {
          cellState = 'fixed';
        }

        if (cellState === 'selected') counts[lead._id]++;
        cells[lead._id] = {
          display: cell.display,
          different: cell.different,
          state: cellState,
          canAlternate: CONTACT_FIELD_KEYS.has(row.key) && lead._id !== keepId && !cell.blank && hasNewContactDetails(lead._id),
          isAlternate,
        };
      }
      return { key: row.key, label: row.label, locked, cells };
    });

    return { rows, counts };
  }

  /** Tick or untick one lead's value for a field. */
  toggleCell(row: GridRow, leadId: LeadId): void {
    const cell = row.cells[leadId];
    const comparison = this.store.snapshot.comparison;
    const keepId = this.store.snapshot.keepLeadId;
    if (!comparison || row.locked || !cell || (cell.state !== 'available' && cell.state !== 'selected')) return;

    const mergeable = comparison.mergeables.find(field => field.key === row.key);
    if (mergeable) {
      const isDuplicateTicked = (): boolean => mergeable.candidates.some(entry => this.store.snapshot.entrySelection[row.key]?.[entry.id]);
      if (leadId === keepId) {
        // Dropping the main lead's own entries only means something when another lead's replace them.
        this.store.setEntryMode(row.key, cell.state === 'selected' && isDuplicateTicked() ? 'overwrite' : 'merge');
        return;
      }
      const next = cell.state !== 'selected';
      mergeable.candidates.filter(entry => entry.source._id === leadId).forEach(entry => this.store.setEntrySelected(row.key, entry.id, next));
      if (!isDuplicateTicked()) this.store.setEntryMode(row.key, 'merge');
      return;
    }

    if (leadId === keepId || cell.state === 'selected') {
      this.store.setScalarSelected(row.key, false);
      return;
    }
    const scalar = comparison.scalars.find(field => field.key === row.key);
    const display = cell.display.trim().toLowerCase();
    const alt = scalar?.alternatives.find(item => item.display.trim().toLowerCase() === display);
    if (alt) this.store.setScalarValue(row.key, alt.value, leadId);
  }

  // ---- confirm / skip / cancel -------------------------------------------

  confirmMerge(): void {
    const s = this.store.snapshot;
    const keepLead = this.store.keepLead;
    if (!keepLead || !s.comparison) return;
    if (this.store.selectedCount === 0) {
      this.toaster.Warning('Select at least one field to merge.');
      return;
    }

    const payload = this.mergeService.buildPayload({
      keepLead,
      comparison: s.comparison,
      scalarSelection: s.scalarSelection,
      entrySelection: s.entrySelection,
      entryMode: s.entryMode,
      duplicateLeads: this.store.duplicates,
      mergedAt: new Date().toISOString(),
    });

    this.store.setSaving(true);
    this.mergeService
      .execute(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: result => {
          this.store.setSaving(false);
          this.toaster.Success('Leads Merged', 'The selected information was copied to the main lead.');
          this.merged.emit(result);
          this.close();
        },
        error: err => {
          this.store.setSaving(false);
          this.toaster.Error(this.apiErrorMessage(err, 'Could not merge the leads. Please try again.'));
        },
      });
  }

  /**
   * The single primary action: copy what's ticked and close the duplicates, or —
   * when nothing is ticked — just close them.
   */
  confirmOrClose(): void {
    if (!this.canCloseDuplicates) return;
    this.saveAlternates(() => {
      if (this.store.selectedCount > 0) this.confirmMerge();
      else this.skipAndClose();
    });
  }

  /** Surface the backend's own error message ({ message } / { error }) in a toast. */
  private apiErrorMessage(err: any, fallback: string): string {
    return err?.error?.message || err?.error?.error || err?.message || fallback;
  }

  /** Skip copying but still flag the duplicates and keep the chosen (or source) lead. */
  skipAndClose(): void {
    const keepId = this.store.snapshot.keepLeadId ?? this.sourceLeadId;
    const candidates = this.store.snapshot.candidates;
    const dupLeads = candidates.filter(l => l._id !== keepId);
    const duplicateIds = dupLeads.map(l => l._id);
    if (!keepId || duplicateIds.length === 0) {
      this.close();
      return;
    }

    // Full-object update (same shape as the edit-lead save); back-fill required
    // fields from siblings so the validator never 422s on an empty enquiry_type.
    const defaults = requiredDefaultsFrom(candidates);
    const updates = dupLeads.map(dup =>
      this.leadsApi.updateLead(dup._id, toLeadUpdateBody(dup, { status_type: DUPLICATE_LEAD_STATUS, duplicate_of: keepId }, defaults))
    );
    this.store.setSaving(true);
    forkJoin(updates)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.store.setSaving(false);
          this.toaster.Success('Duplicates Closed');
          this.skipped.emit({ keepLeadId: keepId, duplicateLeadIds: duplicateIds });
          this.close();
        },
        error: err => {
          this.store.setSaving(false);
          this.toaster.Error(this.apiErrorMessage(err, 'Could not close the duplicates. Please try again.'));
        },
      });
  }

  cancel(): void {
    this.cancelled.emit();
    this.close();
  }

  onDialogHide(): void {
    // p-dialog closed via the header X / mask — treat as cancel unless saving.
    if (!this.store.snapshot.saving) this.cancel();
  }

  private close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.leadOptions = [];
    this.alternateLeadIds$.next([]);
    this.menuLeadId = null;
    this.store.reset();
  }

  // ---- template helpers ---------------------------------------------------

  /** Render any raw lead value (scalar / populated ref / array) as text. */
  displayText = (value: any): string => displayValue(value);

  /** Main lead wording for the merge summary, e.g. "Lead 2". */
  get keepLeadLabel(): string {
    return this.leadLabel(this.store.snapshot.keepLeadId);
  }

  /** Grid cell for one field/lead pair. */
  cellOf(row: ComparisonRow, leadId: LeadId): ComparisonCell {
    return row.cells[leadId] ?? { display: '', blank: true, different: false };
  }

  /** Two-letter avatar initials from a lead's name. */
  initials = (lead: DuplicateLead): string => {
    const parts = leadName(lead).trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] ?? '';
    const second = parts.length > 1 ? parts[parts.length - 1][0] : (parts[0]?.[1] ?? '');
    return (first + second).toUpperCase() || '?';
  };

  /**
   * True when this lead's email isn't the kept lead's email (case/space-insensitive). Peers can be
   * matched on more than the primary email, so a different address must stand out in red rather
   * than read as "same person, same email".
   */
  isDifferentEmail(lead: DuplicateLead, keepLeadId: LeadId): boolean {
    if (!lead || lead._id === keepLeadId) {
      return false;
    }
    const normalize = (email: any): string => (email ?? '').toString().trim().toLowerCase();
    const keepEmail = normalize(this.store.snapshot.candidates.find(l => l._id === keepLeadId)?.email);
    return !!keepEmail && normalize(lead.email) !== keepEmail;
  }

  /** The lead the modal suggests keeping (flagged in the grid header + dropdown). */
  isRecommended(leadId: LeadId): boolean {
    return !!this.leadOptions.find(option => option.value === leadId)?.recommended;
  }

  /** "Lead 1 / Lead 2 …" — the ordinal used everywhere a lead is referenced. */
  leadLabel = (leadId: LeadId): string => {
    const index = this.store.snapshot.candidates.findIndex(l => l._id === leadId);
    return index >= 0 ? `Lead ${index + 1}` : 'another lead';
  };

  /** Colour class for a lead's status chip. */
  badgeClass = (lead: DuplicateLead): string => {
    const s = displayValue(lead.lead_status).toLowerCase();
    if (/wait|future|no response|pending|overdue|lost/.test(s)) return 'is-amber';
    if (/contact|progress|active|won|qualified/.test(s)) return 'is-green';
    return 'is-neutral';
  };

  trackByLeadId = (_: number, lead: DuplicateLead): LeadId => lead._id;
  trackByLeadIdValue = (_: number, leadId: LeadId): LeadId => leadId;
  trackByFieldKey = (_: number, field: { key: string }): string => field.key;
}
